import { type Dispatch, createContext, useContext, useEffect, useState } from 'react';
import { useAuth } from '../hooks/useAuth';
import { defaultLng, type Lang as I18nLang } from '../i18n';

const GLOBAL_ENTRY_HOST = process.env.REACT_APP_GLOBAL_ENTRY_HOST;
const ENTRY_HOST = process.env.REACT_APP_ENTRY_HOST;

export const LOCAL_STORAGE_KEYS = {
  DARK_MODE: 'darkMode',
} as const;
export type LocalStorageKeys = (typeof LOCAL_STORAGE_KEYS)[keyof typeof LOCAL_STORAGE_KEYS];

export type Lang = 'en' | 'ja';
export interface AppContextType {
  darkMode: boolean;
  setDarkMode: (darkMode: boolean) => void;
  userId: string;
  selectedAuditionTitle: string;
  setSelectedAuditionTitle: (auditionName: string) => void;
  currentLang: I18nLang;
  setCurrentLang: Dispatch<I18nLang>;
  langs: I18nLang[];
  setLangs: Dispatch<I18nLang[]>;
  isGlobal: boolean;
}

export const AppContext = createContext<AppContextType>({
  darkMode: Boolean(localStorage.getItem(LOCAL_STORAGE_KEYS.DARK_MODE) === 'true'),
  setDarkMode: () => {},
  userId: '',
  selectedAuditionTitle: '',
  setSelectedAuditionTitle: () => {},
  currentLang: defaultLng,
  setCurrentLang: () => {},
  langs: [],
  setLangs: () => {},
  isGlobal: window.location.origin === GLOBAL_ENTRY_HOST,
});

export const AppContextProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const context: AppContextType = useContext(AppContext);
  const [darkMode, setDarkMode] = useState(context.darkMode);

  const [selectedAuditionTitle, setSelectedAuditionTitle] = useState('');

  const auth = useAuth();
  const userId = auth.username;

  const [langs, setLangs] = useState<Lang[]>(['en', 'ja']);
  const [currentLang, setCurrentLang] = useState<I18nLang>(defaultLng);

  const newContext: AppContextType = {
    darkMode,
    setDarkMode: (darkMode: boolean) => {
      localStorage.setItem(LOCAL_STORAGE_KEYS.DARK_MODE, String(darkMode));
      setDarkMode(darkMode);
    },
    userId,
    selectedAuditionTitle,
    setSelectedAuditionTitle: (auditionName: string) => {
      setSelectedAuditionTitle(auditionName);
    },
    currentLang,
    setCurrentLang: (lang: I18nLang) => {
      setCurrentLang(lang);
    },
    langs,
    setLangs: (langs: I18nLang[]) => {
      setLangs(langs);
    },
    isGlobal: context.isGlobal,
  };

  //TODO うまくブラウザの設定をMediaQueryで取得できないので、一旦コメントアウト
  //useEffect(() => { setDarkMode(prefersDarkMode) }, [])

  useEffect(() => {
    const body = document.body;
    if (darkMode) {
      body.classList.add('dark-theme');
    } else {
      body.classList.remove('dark-theme');
    }
  }, [darkMode]);

  return <AppContext.Provider value={newContext}>{children}</AppContext.Provider>;
};
